import {IntlShape, defineMessages} from 'react-intl'

/**
 * Catálogo de Configuración de canales (`/channel-config`) y de los componentes
 * que comparte con la pestaña de IA de los grupos de asignación.
 *
 * Casi todo este módulo estaba **hardcodeado en español**. Siguiendo §6 del
 * handoff, el inglés se redacta como fuente y el español se conserva tal cual
 * venía. Única excepción: se normalizó el voseo suelto ("Elegí") al registro de
 * usted que usa el resto de la app.
 *
 * No se traduce:
 * - **Dify, Twilio, Anthropic, OpenAI, Claude, GPT, Meta, WhatsApp, SMS,
 *   VICIdial, PolicyDial**: proveedores, productos y canales.
 * - **API Key, API URL, App ID, Account SID, Auth Token, webhook, JWT, SMTP,
 *   TLS**: términos técnicos que el equipo usa en inglés. **Trunk** no: se
 *   traduce "troncal" (glosario §17.14).
 * - Los **estados de mensaje** (`SENT`, `DELIVERED`, `READ`, `FAILED`,
 *   `RECEIVED`) y las direcciones en crudo: los sirve el backend.
 * - Los identificadores de modelo (`claude-sonnet-5`, `gpt-5`) y los valores de
 *   ejemplo de los placeholders (`AC...`, `smtp.gmail.com`, `app-xxxx…`).
 */
export const EM_DASH = '—'

/** Ojo abierto/cerrado del botón que muestra u oculta una API Key. */
export const EYE_SHOWN = '👁️‍🗨️'
export const EYE_HIDDEN = '🔒'

/** Marca de campo obligatorio. */
export const REQUIRED_MARK = '*'

export const CHANNEL_CONFIG_MESSAGES = defineMessages({
  // Selector de tres estados del Asistente de IA. Eran un const a nivel de
  // modulo (`TRI_STATE_OPTIONS`) que se pintaba como `{option.label}`, asi que
  // el rule no lo veia. Los ids van en `common.*`: los tres textos aparecian ya
  // repetidos, feature-scoped, en tres namespaces distintos.
  triStateInherit: {id: 'common.state.inherit', defaultMessage: 'Inherit'},
  triStateEnabled: {id: 'common.state.enabled', defaultMessage: 'Enabled'},
  triStateDisabled: {id: 'common.state.disabled', defaultMessage: 'Disabled'},
  // ── Modal de mensajes del canal ──
  messagesTitle: {
    id: 'telephony.channelConfig.messages.title',
    defaultMessage: '{channel} messages',
  },
  messagesSearchLabel: {
    id: 'telephony.channelConfig.messages.searchLabel',
    defaultMessage: 'Search text',
  },
  messagesSearchPlaceholder: {
    id: 'telephony.channelConfig.messages.searchPlaceholder',
    defaultMessage: 'Search in the message content...',
  },
  messagesAllStatuses: {
    id: 'telephony.channelConfig.messages.allStatuses',
    defaultMessage: 'All statuses',
  },
  messagesAllDirections: {
    id: 'telephony.channelConfig.messages.allDirections',
    defaultMessage: 'All directions',
  },
  messagesDateFrom: {id: 'common.field.dateFrom', defaultMessage: 'From date'},
  messagesDateTo: {id: 'common.field.dateTo', defaultMessage: 'To date'},
  messagesSyncing: {
    id: 'telephony.channelConfig.messages.syncing',
    defaultMessage: 'Syncing with Twilio...',
  },
  messagesSyncedJustNow: {
    id: 'telephony.channelConfig.messages.syncedJustNow',
    defaultMessage: 'Just synced with Twilio ({created} new, {updated} updated).',
  },
  messagesSyncsOnSearch: {
    id: 'telephony.channelConfig.messages.syncsOnSearch',
    defaultMessage: 'It syncs with Twilio automatically when you search.',
  },
  messagesLoading: {
    id: 'telephony.channelConfig.messages.loading',
    defaultMessage: 'Loading messages...',
  },
  messagesLoadError: {
    id: 'telephony.channelConfig.messages.loadError',
    defaultMessage: 'Error loading messages: {message}',
  },
  messagesEmpty: {
    id: 'telephony.channelConfig.messages.empty',
    defaultMessage: 'No messages found for this channel with the current filters.',
  },
  messagesColDate: {id: 'telephony.channelConfig.messages.column.date', defaultMessage: 'Date'},
  messagesColAssignedTo: {
    id: 'telephony.channelConfig.messages.column.assignedTo',
    defaultMessage: 'Assigned To',
  },
  messagesUnassigned: {
    id: 'telephony.channelConfig.messages.unassigned',
    defaultMessage: 'Unassigned',
  },
  messagesSent: {id: 'telephony.channelConfig.messages.sent', defaultMessage: 'Sent'},
  messagesReceived: {id: 'telephony.channelConfig.messages.received', defaultMessage: 'Received'},
  messagesNoContent: {
    id: 'telephony.channelConfig.messages.noContent',
    defaultMessage: 'No content',
  },
  messagesLoadMore: {
    id: 'telephony.channelConfig.messages.loadMore',
    defaultMessage: 'Load more',
  },
  messagesSyncFailed: {
    id: 'telephony.channelConfig.messages.toast.syncFailed',
    defaultMessage: 'Could not sync with Twilio: {error}',
  },
  messagesSyncFailedLocal: {
    id: 'telephony.channelConfig.messages.toast.syncFailedLocal',
    defaultMessage: 'Could not sync with Twilio; showing the local history.',
  },

  // ── Sección de IA conversacional ──
  aiTitle: {id: 'telephony.channelConfig.ai.title', defaultMessage: 'Conversational AI'},
  aiDirectLlm: {id: 'telephony.channelConfig.ai.directLlm', defaultMessage: 'Direct LLM'},
  aiBadgeDirectLlmWith: {
    id: 'telephony.channelConfig.ai.badge.directLlmWith',
    defaultMessage: 'Direct LLM ({model})',
  },
  aiEnableLabel: {
    id: 'telephony.channelConfig.ai.enableLabel',
    defaultMessage: 'Enable Conversational AI at this level',
  },
  aiEnableHint: {
    id: 'telephony.channelConfig.ai.enableHint',
    defaultMessage:
      'When enabled, agents can use conversational AI (Dify or a direct LLM) to generate automatic replies.',
  },
  aiProvider: {id: 'telephony.channelConfig.ai.provider', defaultMessage: 'AI provider'},
  aiProviderHint: {
    id: 'telephony.channelConfig.ai.providerHint',
    defaultMessage:
      'Choose whether this level uses Dify (engine + external knowledge base) or a direct LLM (Anthropic/OpenAI) with your own credentials.',
  },
  aiApiKeyHint: {
    id: 'telephony.channelConfig.ai.apiKeyHint',
    defaultMessage: 'Get your API Key from your <a>Dify dashboard</a>',
  },
  aiApiUrlHint: {
    id: 'telephony.channelConfig.ai.apiUrlHint',
    defaultMessage: 'Default: {url}',
  },
  aiAppIdPlaceholder: {
    id: 'telephony.channelConfig.ai.appIdPlaceholder',
    defaultMessage: 'The app UUID in Dify',
  },
  aiAppIdHint: {
    id: 'telephony.channelConfig.ai.appIdHint',
    defaultMessage: 'Required to route messages to the right App.',
  },
  aiTesting: {id: 'telephony.channelConfig.ai.testing', defaultMessage: 'Testing...'},
  aiTestConnection: {
    id: 'telephony.channelConfig.ai.testConnection',
    defaultMessage: '💾 Test connection',
  },
  aiTestHint: {
    id: 'telephony.channelConfig.ai.testHint',
    defaultMessage: 'Checks that your API Key is valid',
  },
  aiShowKey: {id: 'telephony.channelConfig.ai.showKey', defaultMessage: 'Show API Key'},
  aiHideKey: {id: 'telephony.channelConfig.ai.hideKey', defaultMessage: 'Hide API Key'},
  aiLlmProvider: {id: 'telephony.channelConfig.ai.llmProvider', defaultMessage: 'LLM provider'},
  aiModel: {id: 'telephony.channelConfig.ai.model', defaultMessage: 'Model'},
  aiModelHint: {id: 'telephony.channelConfig.ai.modelHint', defaultMessage: 'e.g. {model}'},
  aiNote: {
    id: 'telephony.channelConfig.ai.note',
    defaultMessage:
      '<b>💡 Note:</b> Agents can set their own Conversational AI configuration in their profile, which overrides this one.',
  },
  aiDisabledReason: {
    id: 'telephony.channelConfig.ai.disabledReason',
    defaultMessage: '⚠️ {reason}',
  },
  aiDisabledNotice: {
    id: 'telephony.channelConfig.ai.disabledNotice',
    defaultMessage:
      'ℹ️ Conversational AI is disabled. Turn it on to use AI-powered conversations (Dify or Direct LLM).',
  },
  aiFillKeyAndUrl: {
    id: 'telephony.channelConfig.ai.toast.fillKeyAndUrl',
    defaultMessage: 'Please fill in API Key and URL',
  },
  aiConnectionOk: {
    id: 'telephony.channelConfig.ai.connectionOk',
    defaultMessage: '✅ Connection verified successfully',
  },
  aiConnectionError: {
    id: 'telephony.channelConfig.ai.connectionError',
    defaultMessage: '❌ Error: {message}',
  },
  aiConnectedToast: {
    id: 'telephony.channelConfig.ai.toast.connected',
    defaultMessage: 'Connected to Dify successfully',
  },
  aiConnectFailedToast: {
    id: 'telephony.channelConfig.ai.toast.connectFailed',
    defaultMessage: 'Could not connect to Dify: {message}',
  },
  aiInvalidApiKey: {
    id: 'telephony.channelConfig.ai.invalidApiKey',
    defaultMessage: 'Invalid API Key',
  },
  aiGenericConnectionError: {
    id: 'telephony.channelConfig.ai.genericConnectionError',
    defaultMessage: 'Connection error',
  },

  // ── Panel de configuración de la organización ──
  messagingSubtitle: {
    id: 'telephony.channelConfig.messagingSubtitle',
    defaultMessage: 'Configure active messaging integrations, credentials and webhooks',
  },
  twilioSmsDisabled: {
    id: 'telephony.channelConfig.twilioSmsDisabled',
    defaultMessage: 'Twilio SMS is currently disabled for this organization.',
  },
  whatsappPickProvider: {
    id: 'telephony.channelConfig.whatsappPickProvider',
    defaultMessage: 'Select a WhatsApp provider to configure your channel credentials.',
  },
  reuseSmsAccount: {
    id: 'telephony.channelConfig.reuseSmsAccount',
    defaultMessage: 'Leave blank to reuse the SMS Twilio account above.',
  },
  botmakerApiToken: {
    id: 'telephony.channelConfig.botmakerApiToken',
    defaultMessage: 'API Token (JWT)',
  },

  // ── Sección de asistente de IA ──
  assistantTitle: {
    id: 'telephony.channelConfig.assistant.title',
    defaultMessage: 'AI Assistant',
  },
  assistantEnableLabel: {
    id: 'telephony.channelConfig.assistant.enableLabel',
    defaultMessage: 'Enable AI Assistant for this organization',
  },
  assistantEnableHint: {
    id: 'telephony.channelConfig.assistant.enableHint',
    defaultMessage:
      'Internal AI assistant widget available to agents for answering questions across configured domains.',
  },
  assistantDomains: {
    id: 'telephony.channelConfig.assistant.domains',
    defaultMessage: 'Domains',
  },
  assistantDomainsHint: {
    id: 'telephony.channelConfig.assistant.domainsHint',
    defaultMessage:
      'Choose which knowledge domains the AI Assistant can use to answer questions at this level.',
  },
  assistantLoadingDomains: {
    id: 'telephony.channelConfig.assistant.loadingDomains',
    defaultMessage: 'Loading domains…',
  },
  assistantNoDomains: {
    id: 'telephony.channelConfig.assistant.noDomains',
    defaultMessage: 'No domains are configured yet.',
  },

  // ── Sección de prompt ──
  promptTitle: {
    id: 'telephony.channelConfig.prompt.title',
    defaultMessage: 'Conversational AI System Prompt',
  },
  promptScopeCustom: {
    id: 'telephony.channelConfig.prompt.scope.customization',
    defaultMessage: 'Customization',
  },
  promptHeaderLabel: {
    id: 'telephony.channelConfig.prompt.header',
    defaultMessage: 'Header (initial persona & system role)',
  },
  promptOrgOnly: {
    id: 'telephony.channelConfig.prompt.orgOnly',
    defaultMessage: '— editable at organization level only',
  },
  promptHeaderPlaceholder: {
    id: 'telephony.channelConfig.prompt.headerPlaceholder',
    defaultMessage:
      'e.g.: You are an insurance advisor. Respond in a friendly, professional, and concise manner.',
  },
  promptBodyLabel: {
    id: 'telephony.channelConfig.prompt.body',
    defaultMessage: 'Body (level-specific instructions)',
  },
  promptBodyPlaceholder: {
    id: 'telephony.channelConfig.prompt.bodyPlaceholder',
    defaultMessage:
      'Additional instructions for this level. Appended to the rest of the inheritance chain.',
  },
  promptBodyHint: {
    id: 'telephony.channelConfig.prompt.bodyHint',
    defaultMessage:
      'The final prompt combines organization, group, and agent body texts — this text is appended and does not overwrite organization defaults.',
  },
  promptFooterLabel: {
    id: 'telephony.channelConfig.prompt.footer',
    defaultMessage: 'Footer (constraints & safety limitations)',
  },
  promptFooterPlaceholder: {
    id: 'telephony.channelConfig.prompt.footerPlaceholder',
    defaultMessage: 'e.g.: Do not invent information that is not available.',
  },
  promptShareTitle: {
    id: 'telephony.channelConfig.prompt.shareTitle',
    defaultMessage: 'Customer context shared with the LLM',
  },
  promptShareHint: {
    id: 'telephony.channelConfig.prompt.shareHint',
    defaultMessage:
      'Only referential context is shared (never sensitive data like SSN, financial amounts, or document files).',
  },
  promptShareContact: {
    id: 'telephony.channelConfig.prompt.share.contact',
    defaultMessage: 'Contact (name & status) — default',
  },
  promptShareQuotes: {
    id: 'telephony.channelConfig.prompt.share.quotes',
    defaultMessage: 'Quotes (status, summary)',
  },
  promptSharePolicies: {
    id: 'telephony.channelConfig.prompt.share.policies',
    defaultMessage: 'Policies (type & status, without premiums or IDs)',
  },
  promptShareLeads: {
    id: 'telephony.channelConfig.prompt.share.leads',
    defaultMessage: 'Leads (stage & intent)',
  },
  promptShareConsent: {
    id: 'telephony.channelConfig.prompt.share.consent',
    defaultMessage: 'Consent status (signed/pending, without document files)',
  },

  // ── Página: cabecera y pestañas ──
  pageTitle: {id: 'telephony.channelConfig.page.title', defaultMessage: 'Channel Configuration'},
  pageSubtitle: {
    id: 'telephony.channelConfig.page.subtitle',
    defaultMessage: 'Configure communication channels for your organization',
  },
  pageWebhookToken: {
    id: 'telephony.channelConfig.page.webhookToken',
    defaultMessage: 'Webhook: <code>{token}</code>',
  },
  tabSettings: {id: 'telephony.channelConfig.page.tab.settings', defaultMessage: 'Settings'},
  tabAllChannels: {
    id: 'telephony.channelConfig.page.tab.allChannels',
    defaultMessage: 'Organization Channels',
  },
  permissionDeniedTitle: {
    id: 'telephony.channelConfig.page.permissionDenied.title',
    defaultMessage: 'Permission Denied',
  },
  permissionDeniedBody: {
    id: 'telephony.channelConfig.page.permissionDenied.body',
    defaultMessage:
      'You do not have permission to configure organization channels. Please contact your administrator.',
  },
  enabled: {id: 'telephony.channelConfig.enabled', defaultMessage: 'Enabled'},
  disabled: {id: 'telephony.channelConfig.disabled', defaultMessage: 'Disabled'},

  // ── Pestaña de canales de la organización ──
  sectionAssignmentGroup: {
    id: 'telephony.channelConfig.section.assignmentGroup',
    defaultMessage: 'Assignment Group',
  },
  sectionUsers: {id: 'common.field.users', defaultMessage: 'Users'},
  ownerGroup: {id: 'common.field.group', defaultMessage: 'Group'},
  colOwnerInfo: {
    id: 'telephony.channelConfig.column.ownerInfo',
    defaultMessage: 'Owner information',
  },
  colChannel: {id: 'telephony.channelConfig.column.channel', defaultMessage: 'Channel'},
  sidMasked: {id: 'telephony.channelConfig.sidMasked', defaultMessage: 'SID {sid}'},
  copyFullUrl: {id: 'telephony.channelConfig.copyFullUrl', defaultMessage: 'Copy the full URL'},
  copyWebhookUrl: {
    id: 'telephony.channelConfig.copyWebhookUrl',
    defaultMessage: 'Copy webhook URL',
  },
  copyStatusUrl: {
    id: 'telephony.channelConfig.copyStatusUrl',
    defaultMessage: 'Copy status callback URL',
  },
  copyVerifyToken: {
    id: 'telephony.channelConfig.copyVerifyToken',
    defaultMessage: 'Copy verify token',
  },
  issueReview: {id: 'telephony.channelConfig.issue.review', defaultMessage: 'Review'},
  viewMessages: {id: 'telephony.channelConfig.viewMessages', defaultMessage: 'View messages'},
  viewMessagesHint: {
    id: 'telephony.channelConfig.viewMessagesHint',
    defaultMessage: 'See this channel\u2019s message history.',
  },
  viewMessagesEmailHint: {
    id: 'telephony.channelConfig.viewMessagesEmailHint',
    defaultMessage: 'Message history is not available for Email yet.',
  },
  noChannelsInSection: {
    id: 'telephony.channelConfig.noChannelsInSection',
    defaultMessage: 'No channels configured in this section.',
  },

  // ── Tabla de canales de la organización ──
  viewCards: {id: 'telephony.channelConfig.view.cards', defaultMessage: 'Cards'},
  viewTable: {id: 'telephony.channelConfig.view.table', defaultMessage: 'Table'},
  // Dos plurales no caben en un mismo mensaje (`no-multiple-plurals`), así que el
  // resumen se arma con una llave por magnitud más el separador.
  ownerCount: {
    id: 'telephony.channelConfig.ownerCount',
    defaultMessage: '{count, plural, one {# owner} other {# owners}}',
  },
  channelCount: {
    id: 'telephony.channelConfig.channelCount',
    defaultMessage: '{count, plural, one {# channel} other {# channels}}',
  },
  ownerChannelCount: {
    id: 'telephony.channelConfig.ownerChannelCount',
    defaultMessage: '{owners} • {channels}',
  },
  webhookTag: {id: 'telephony.channelConfig.webhookTag', defaultMessage: 'WEBHOOK'},
  sidTag: {id: 'telephony.channelConfig.sidTag', defaultMessage: 'SID:'},

  // ── Selector de organización ──
  selectOrganization: {
    id: 'telephony.channelConfig.selectOrganization',
    defaultMessage: 'Select organization...',
  },
  configuringOrg: {
    id: 'telephony.channelConfig.configuringOrg',
    defaultMessage: 'Configuring: <b>{name}</b>',
  },

  // ── Sistema de telefonía ──
  sectionTelephony: {
    id: 'telephony.channelConfig.section.telephony',
    defaultMessage: 'Telephony System',
  },
  configuration: {id: 'telephony.channelConfig.configuration', defaultMessage: 'Configuration'},
  enablePolicyDial: {
    id: 'telephony.channelConfig.enablePolicyDial',
    defaultMessage: 'Enable PolicyDial telephony',
  },
  webphoneUrlHint: {
    id: 'telephony.channelConfig.webphoneUrlHint',
    defaultMessage: 'Used as fallback when the AGENT API cannot return the active session URL.',
  },
  apiUser: {id: 'telephony.channelConfig.apiUser', defaultMessage: 'API User'},
  apiPassword: {id: 'telephony.channelConfig.apiPassword', defaultMessage: 'API Password'},
  apiPasswordPlaceholder: {
    id: 'telephony.channelConfig.apiPasswordPlaceholder',
    defaultMessage: 'Enter API password',
  },
  defaultAgentUser: {
    id: 'telephony.channelConfig.defaultAgentUser',
    defaultMessage: 'Default agent user',
  },
  defaultPhoneCode: {
    id: 'telephony.channelConfig.defaultPhoneCode',
    defaultMessage: 'Default phone code',
  },
  campaignSelectDefault: {
    id: 'telephony.channelConfig.campaignSelectDefault',
    defaultMessage: 'Select organization default campaign...',
  },
  campaignCurrentDefault: {
    id: 'telephony.channelConfig.campaignCurrentDefault',
    defaultMessage: 'Current organization default',
  },
  campaignInactiveSuffix: {
    id: 'telephony.channelConfig.campaignInactiveSuffix',
    defaultMessage: '(Inactive)',
  },
  campaignRefresh: {
    id: 'telephony.channelConfig.campaignRefresh',
    defaultMessage: 'Refresh campaigns',
  },
  campaignLoad: {id: 'telephony.channelConfig.campaignLoad', defaultMessage: 'Load campaigns'},
  campaignHint: {
    id: 'telephony.channelConfig.campaignHint',
    defaultMessage:
      'Super admins can set the organization default here. Users can still choose their active PolicyDial campaign from <code>Integrations</code>.',
  },
  dialDelay: {
    id: 'telephony.channelConfig.dialDelay',
    defaultMessage: 'Auto dial delay (seconds)',
  },
  dialDelayHint: {
    id: 'telephony.channelConfig.dialDelayHint',
    defaultMessage:
      'Wait time between saving the disposition and placing the next PolicyDial queue call.',
  },

  // ── Mensajería y canales ──
  sectionMessaging: {
    id: 'telephony.channelConfig.section.messaging',
    defaultMessage: 'Messaging & Channels',
  },
  elasticTrunkHint: {
    id: 'telephony.channelConfig.elasticTrunkHint',
    defaultMessage: 'Trunk for purchased numbers (Caller ID / Replace) routed to VICIdial.',
  },
  labelInbound: {id: 'telephony.channelConfig.label.inbound', defaultMessage: 'Inbound:'},
  labelStatus: {id: 'telephony.channelConfig.label.status', defaultMessage: 'Status:'},
  labelWebhook: {id: 'telephony.channelConfig.label.webhook', defaultMessage: 'Webhook:'},
  labelVerifyToken: {
    id: 'telephony.channelConfig.label.verifyToken',
    defaultMessage: 'Verify Token:',
  },
  channelId: {id: 'telephony.channelConfig.channelId', defaultMessage: 'Channel ID'},
  phoneLine: {id: 'telephony.channelConfig.phoneLine', defaultMessage: 'Phone Line'},
  whatsappNumber: {id: 'telephony.channelConfig.whatsappNumber', defaultMessage: 'WhatsApp #'},
  phoneId: {id: 'telephony.channelConfig.phoneId', defaultMessage: 'Phone ID'},

  // ── Email ──
  sectionEmail: {id: 'telephony.channelConfig.section.email', defaultMessage: 'Email System'},
  smtpHost: {id: 'telephony.channelConfig.smtpHost', defaultMessage: 'SMTP Host'},
  port: {id: 'telephony.channelConfig.port', defaultMessage: 'Port'},
  password: {id: 'telephony.channelConfig.password', defaultMessage: 'Password'},

  // ── Integraciones ──
  sectionIntegrations: {
    id: 'telephony.channelConfig.section.integrations',
    defaultMessage: 'Integrations',
  },
  agentEmail: {id: 'telephony.channelConfig.agentEmail', defaultMessage: 'Agent Email'},
  webhookSecret: {
    id: 'telephony.channelConfig.webhookSecret',
    defaultMessage: 'Webhook Secret',
  },

  // ── Toasts de la página ──
  toastWebhookCopied: {
    id: 'telephony.channelConfig.toast.webhookCopied',
    defaultMessage: 'Webhook URL copied',
  },
  toastStatusUrlCopied: {
    id: 'telephony.channelConfig.toast.statusUrlCopied',
    defaultMessage: 'Status callback URL copied',
  },
  toastVerifyTokenCopied: {
    id: 'telephony.channelConfig.toast.verifyTokenCopied',
    defaultMessage: 'Verify token copied',
  },
  toastMustBeLoggedIn: {
    id: 'telephony.channelConfig.toast.mustBeLoggedIn',
    defaultMessage: 'You must be logged in to load PolicyDial campaigns.',
  },
  toastCampaignCredsRequired: {
    id: 'telephony.channelConfig.toast.campaignCredsRequired',
    defaultMessage:
      'Base URL, API User and API Password are required to load PolicyDial campaigns.',
  },
  toastNoCampaigns: {
    id: 'telephony.channelConfig.toast.noCampaigns',
    defaultMessage: 'No PolicyDial campaigns were returned for this organization.',
  },
  toastCampaignsLoaded: {
    id: 'telephony.channelConfig.toast.campaignsLoaded',
    defaultMessage: 'PolicyDial campaigns loaded successfully.',
  },
  toastCampaignsFailed: {
    id: 'telephony.channelConfig.toast.campaignsFailed',
    defaultMessage: 'Unable to load PolicyDial campaigns.',
  },
  toastConfigSaved: {
    id: 'telephony.channelConfig.toast.configSaved',
    defaultMessage: 'Channel configuration saved successfully',
  },
  toastConfigSaveFailed: {
    id: 'telephony.channelConfig.toast.configSaveFailed',
    defaultMessage: 'Error saving channel configuration: {error}',
  },
  toastChannelCreated: {
    id: 'telephony.channelConfig.toast.channelCreated',
    defaultMessage: 'Channel created',
  },
  toastChannelUpdated: {
    id: 'telephony.channelConfig.toast.channelUpdated',
    defaultMessage: 'Channel updated',
  },
  toastFillTwilioSms: {
    id: 'telephony.channelConfig.toast.fillTwilioSms',
    defaultMessage: 'Please fill in Account SID and Auth Token',
  },
  toastFillTwilioWhatsapp: {
    id: 'telephony.channelConfig.toast.fillTwilioWhatsapp',
    defaultMessage: 'Please fill in Twilio Account SID and Auth Token',
  },
  toastSelectWhatsappProvider: {
    id: 'telephony.channelConfig.toast.selectWhatsappProvider',
    defaultMessage: 'Please select a WhatsApp provider',
  },
  toastFillBotmaker: {
    id: 'telephony.channelConfig.toast.fillBotmaker',
    defaultMessage: 'Please fill in API Token and Default Channel ID',
  },
  toastFillMeta: {
    id: 'telephony.channelConfig.toast.fillMeta',
    defaultMessage: 'Please fill in Access Token and Phone Number ID',
  },
  validationFailed: {
    id: 'telephony.channelConfig.validationFailed',
    defaultMessage: 'Validation failed',
  },
  validationError: {
    id: 'telephony.channelConfig.validationError',
    defaultMessage: 'Validation error',
  },

  // ── Reusadas de common.* ──
  status: {id: 'common.field.status', defaultMessage: 'Status'},
  contact: {id: 'common.field.contact', defaultMessage: 'Contact'},
  message: {id: 'common.field.message', defaultMessage: 'Message'},
  organization: {id: 'common.field.organization', defaultMessage: 'Organization'},
  refresh: {id: 'common.action.refresh', defaultMessage: 'Refresh'},
  colPhoneNumber: {id: 'common.field.phoneNumber', defaultMessage: 'Phone Number'},
  search: {id: 'common.action.search', defaultMessage: 'Search'},
  close: {id: 'common.action.close', defaultMessage: 'Close'},
  edit: {id: 'common.action.edit', defaultMessage: 'Edit'},
  saveChanges: {id: 'common.action.saveChanges', defaultMessage: 'Save changes'},
  loading: {id: 'common.loading', defaultMessage: 'Loading...'},
  saving: {id: 'common.state.saving', defaultMessage: 'Saving...'},
  user: {id: 'common.field.user', defaultMessage: 'User'},
  phone: {id: 'common.field.phone', defaultMessage: 'Phone'},
  from: {id: 'common.field.from', defaultMessage: 'From'},

  // --- Toasts (slice R5.b): texto que sale por toast, invisible para el rule ---
  toastSyncFailedWithError: {
    id: 'telephony.channelConfig.toast.syncFailedWithError',
    defaultMessage: 'Could not sync with Twilio: {message}',
  },
  toastSyncFailedLocal: {
    id: 'telephony.channelConfig.toast.syncFailedLocal',
    defaultMessage: 'Could not sync with Twilio, showing local history.',
  },
  toastAssignFailed: {
    id: 'telephony.channelConfig.toast.assignFailed',
    defaultMessage: 'Could not assign message.',
  },
  toastLoginRequiredCampaigns: {
    id: 'telephony.channelConfig.toast.loginRequiredCampaigns',
    defaultMessage: 'You must be logged in to load PolicyDial campaigns.',
  },
  toastCampaignCredentialsRequired: {
    id: 'telephony.channelConfig.toast.campaignCredentialsRequired',
    defaultMessage:
      'Base URL, API User and API Password are required to load PolicyDial campaigns.',
  },
  toastNoCampaignsReturned: {
    id: 'telephony.channelConfig.toast.noCampaignsReturned',
    defaultMessage: 'No PolicyDial campaigns were returned for this organization.',
  },
  toastConfigSaveError: {
    id: 'telephony.channelConfig.toast.configSaveError',
    defaultMessage: 'Error saving channel configuration: {message}',
  },
  toastFillAccountSid: {
    id: 'telephony.channelConfig.toast.fillAccountSid',
    defaultMessage: 'Please fill in Account SID and Auth Token',
  },
  toastValidationFailed: {
    id: 'telephony.channelConfig.toast.validationFailed',
    defaultMessage: 'Validation failed',
  },
  toastValidationError: {
    id: 'telephony.channelConfig.toast.validationError',
    defaultMessage: 'Validation error',
  },
  toastSelectWhatsAppProvider: {
    id: 'telephony.channelConfig.toast.selectWhatsAppProvider',
    defaultMessage: 'Please select a WhatsApp provider',
  },
  toastFillTwilioCredentials: {
    id: 'telephony.channelConfig.toast.fillTwilioCredentials',
    defaultMessage: 'Please fill in Twilio Account SID and Auth Token',
  },
  toastFillApiTokenChannel: {
    id: 'telephony.channelConfig.toast.fillApiTokenChannel',
    defaultMessage: 'Please fill in API Token and Default Channel ID',
  },
  toastFillAccessTokenPhoneId: {
    id: 'telephony.channelConfig.toast.fillAccessTokenPhoneId',
    defaultMessage: 'Please fill in Access Token and Phone Number ID',
  },
  // Frases completas y no '{item} copiado': en ES la concordancia cambia
  // segun el sustantivo (la URL copiada / el token copiado).
  toastInboundWebhookCopied: {
    id: 'telephony.channelConfig.toast.inboundWebhookCopied',
    defaultMessage: 'Inbound Webhook URL copied',
  },
  toastStatusCallbackCopied: {
    id: 'telephony.channelConfig.toast.statusCallbackCopied',
    defaultMessage: 'Status Callback URL copied',
  },
  toastBotmakerWebhookCopied: {
    id: 'telephony.channelConfig.toast.botmakerWebhookCopied',
    defaultMessage: 'Botmaker Webhook URL copied',
  },
  toastWhatsAppInboundCopied: {
    id: 'telephony.channelConfig.toast.whatsAppInboundCopied',
    defaultMessage: 'WhatsApp Inbound Webhook copied',
  },
  toastMetaWebhookCopied: {
    id: 'telephony.channelConfig.toast.metaWebhookCopied',
    defaultMessage: 'Meta Webhook URL copied',
  },
  toastWebhookUrlCopied: {
    id: 'telephony.channelConfig.toast.webhookUrlCopied',
    defaultMessage: 'Webhook URL copied',
  },
})

/**
 * Términos técnicos y nombres de producto que se escriben igual en EN y ES.
 * Son constantes y no llaves de traducción: el rule los marcaría dentro del
 * JSX, pero traducirlos no cambiaría nada.
 */
export const LABELS = {
  webhook: 'Webhook',
  info: 'Info',
  baseUrl: 'Base URL',
  webphoneUrl: 'Webphone URL',
  accountSid: 'Account SID',
  authToken: 'Auth Token',
  apiToken: 'API Token',
  accessToken: 'Access Token',
  verifyToken: 'Verify Token',
  apiKey: 'API Key',
  elasticTrunkSid: 'Elastic SIP Trunk SID',
  twilioVoiceTag: '· Twilio Voice',
  replyTo: 'Reply-To',
  tagInbound: 'INBOUND',
  tagStatus: 'STATUS',
  tagBotmaker: 'BOTMAKER',
  tagMetaWebhook: 'META WEBHOOK',
  tagVerifyToken: 'VERIFY TOKEN',
  tls: 'TLS',
  emailSmtp: 'Email SMTP',
  twilioSms: 'Twilio SMS',
  whatsapp: 'WhatsApp',
  ringCentral: '☎️ RingCentral',
  policyDial: 'PolicyDial',
  healthSherpa: 'HealthSherpa Medicare',
  passwordDots: '••••••••',
}

/** Tipo de canal: nombres de canal, no se traducen. */
export const CHANNEL_KIND_LABEL: Record<'SMS' | 'WHATSAPP' | 'EMAIL', string> = {
  SMS: 'SMS',
  WHATSAPP: 'WhatsApp',
  EMAIL: 'Email',
}

/** Proveedores de WhatsApp. `NONE` se muestra con la llave `disabled`. */
export const WHATSAPP_PROVIDER_OPTIONS = [
  {value: 'BOTMAKER', label: 'Botmaker'},
  {value: 'TWILIO', label: 'Twilio'},
  {value: 'META', label: 'Meta Cloud API'},
]

/**
 * Orden y etiquetas de las tres secciones de propietario. Viven aquí y no en el
 * componente porque son literales a nivel de módulo: se guardan como
 * descriptores y se formatean en render.
 */
export const SECTION_ORDER = [
  {ownerType: 'organization', title: 'organization' as const, badge: 'organization' as const},
  {ownerType: 'group', title: 'assignmentGroups' as const, badge: 'group' as const},
  {ownerType: 'user', title: 'users' as const, badge: 'user' as const},
]

const SECTION_TITLE = defineMessages({
  organization: {id: 'common.field.organization', defaultMessage: 'Organization'},
  assignmentGroups: {id: 'telephony.groups.title', defaultMessage: 'Assignment Groups'},
  users: {id: 'common.field.users', defaultMessage: 'Users'},
})

const OWNER_LABEL = defineMessages({
  organization: {id: 'common.field.organization', defaultMessage: 'Organization'},
  group: {id: 'common.field.group', defaultMessage: 'Group'},
  user: {id: 'common.field.user', defaultMessage: 'User'},
})

export const sectionTitle = (intl: IntlShape, key: keyof typeof SECTION_TITLE) =>
  intl.formatMessage(SECTION_TITLE[key])

export const ownerLabel = (intl: IntlShape, key: keyof typeof OWNER_LABEL) =>
  intl.formatMessage(OWNER_LABEL[key])

/** Variante de color del badge de propietario. Presentación, no texto. */
export const OWNER_BADGE: Record<string, {variant: string; textClass: string}> = {
  organization: {variant: 'primary', textClass: 'text-white'},
  group: {variant: 'info', textClass: 'text-white'},
  // El "secondary" de Metronic es un gris claro ($gray-300): necesita texto oscuro.
  user: {variant: 'secondary', textClass: 'text-dark'},
}

/** Separador con el que se listan varias incidencias en un mismo tooltip. */
export const ISSUE_SEPARATOR = ' · '

/** Incidencia detectada por el backend sobre un canal. */
const ISSUE = defineMessages({
  duplicate_number: {
    id: 'telephony.channelConfig.issue.duplicateNumber',
    defaultMessage: 'This number is configured on more than one channel',
  },
  sid_mismatch: {
    id: 'telephony.channelConfig.issue.sidMismatch',
    defaultMessage: 'The same number shows a different Account SID across configs',
  },
})

/** Una incidencia nueva del backend se muestra en crudo en vez de desaparecer. */
export const issueLabel = (intl: IntlShape, issue: string): string => {
  const descriptor = ISSUE[issue as keyof typeof ISSUE]
  return descriptor ? intl.formatMessage(descriptor) : issue
}

/** Dirección del mensaje, para el filtro. Los valores viajan a la API. */
const DIRECTION = defineMessages({
  INBOUND: {id: 'telephony.channelConfig.messages.direction.inbound', defaultMessage: 'Inbound'},
  OUTBOUND: {
    id: 'telephony.channelConfig.messages.direction.outbound',
    defaultMessage: 'Outbound',
  },
})

export const DIRECTION_VALUES = Object.keys(DIRECTION) as Array<keyof typeof DIRECTION>

export const directionLabel = (intl: IntlShape, value: string): string => {
  const descriptor = DIRECTION[value as keyof typeof DIRECTION]
  return descriptor ? intl.formatMessage(descriptor) : value
}

/** Proveedores de IA y de LLM: nombres de producto, no se traducen. */
export const AI_PROVIDER_DIFY = 'Dify'
export const LLM_PROVIDER_OPTIONS = [
  {value: 'ANTHROPIC', label: 'Anthropic (Claude)', short: 'Claude'},
  {value: 'OPENAI', label: 'OpenAI (GPT)', short: 'GPT'},
]

/**
 * "0001 - Ventas (Inactive)" en el selector de campañas. El id y el nombre son
 * datos de VICIdial; solo el sufijo de inactiva es texto nuestro.
 */
export const campaignOptionLabel = (
  intl: IntlShape,
  campaign: {campaign_id: string; campaign_name: string; active: string}
) => {
  const base = `${campaign.campaign_id} - ${campaign.campaign_name}`
  return campaign.active === 'Y'
    ? base
    : `${base} ${intl.formatMessage(CHANNEL_CONFIG_MESSAGES.campaignInactiveSuffix)}`
}
